/**
 * usePurchaseInvoiceData Hook
 * Alış faturası veri yükleme ve kaydetme işlemleri
 */

import { useState, useCallback, useEffect } from 'react';
import ownAuthSession from '../../../../services/auth/ownAuthSession';
import { getCompanyInfo } from '../../../../services/migration/companyDataService';
import firebaseDataService from '../../../../services/FirebaseDataService';
import { accountingApi } from '../../../../config/apiClient';
import { ACCOUNTING_API, handleApiError } from '../../../../config/api';
import { Account, Invoice, InvoiceItem } from '../components/types/invoice.types';
import { formatQuantityForDisplay } from '../utils/invoiceCalculations';
import { readInvoiceLot } from '../utils/invoiceLot';
import { loadCompanyActiveCurrencies } from '../utils/loadCompanyActiveCurrencies';
import { useAuth } from '../../../../contexts/AuthContext';

interface UsePurchaseInvoiceDataProps {
  editingInvoiceId: string | null;
  isFromOffer: boolean;
  ettnFromUrl: string | null;
}

export const usePurchaseInvoiceData = ({
  editingInvoiceId,
  isFromOffer,
  ettnFromUrl,
}: UsePurchaseInvoiceDataProps) => {
  const { warehouses, companyName } = useAuth();
  
  // State
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>([]);
  
  // Cari arama state
  const [accountSearchTerm, setAccountSearchTerm] = useState<string>('');
  const [accountsPage, setAccountsPage] = useState<number>(0);
  const [accountsRowsPerPage, setAccountsRowsPerPage] = useState<number>(10);
  
  // Modal state'leri
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isDraftModalOpen, setIsDraftModalOpen] = useState<boolean>(false);
  const [draftData, setDraftData] = useState<any>(null);
  const [showDraftSavedAlert, setShowDraftSavedAlert] = useState<boolean>(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState<boolean>(false);
  const [isEInvoiceModalOpen, setIsEInvoiceModalOpen] = useState<boolean>(false);
  const [isAccountMatchModalOpen, setIsAccountMatchModalOpen] = useState<boolean>(false);
  
  // E-fatura state'leri
  const [eInvoiceUsername, setEInvoiceUsername] = useState<string>('');
  const [eInvoicePassword, setEInvoicePassword] = useState<string>('');
  const [eInvoiceSupplierInfo, setEInvoiceSupplierInfo] = useState<any>(null);
  
  // Stok işleme modal state'leri
  const [isStockProcessModalOpen, setIsStockProcessModalOpen] = useState<boolean>(false);
  const [stockProcessInvoiceId, setStockProcessInvoiceId] = useState<string | null>(null);
  const [stockProcessCurrentItem, setStockProcessCurrentItem] = useState<number>(0);
  const [stockProcessTotalItems, setStockProcessTotalItems] = useState<number>(0);
  const [stockProcessSuccess, setStockProcessSuccess] = useState<boolean>(false);
  
  // Ürün sayfalama state'leri
  const [itemsPage, setItemsPage] = useState<number>(0);
  const [itemsRowsPerPage, setItemsRowsPerPage] = useState<number>(20);
  const [itemSearchTerm, setItemSearchTerm] = useState<string>('');
  
  // Cari hesapları yükleme - SQL'den
  const loadAccounts = useCallback(async (compId: string) => {
    try {
      console.log('🔄 Cari hesaplar yükleniyor (SQL)...');
      
      const { data: result } = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
      const sqlAccounts = result?.data ?? result ?? [];
      
      const accountsList: Account[] = sqlAccounts
        .filter((data: any) => data.isActive !== false)
        // Yetkili kişi koşulu aranmaz: gerçek kişi carileri AddAccount bilinçli olarak
        // authorizedPerson='' ile kaydeder, aranırsa o cariler listede hiç görünmez.
        .filter((data: any) => data.companyName && data.companyName.trim() !== '')
        .map((data: any) => ({
          id: data.id.toString(),
          companyName: data.companyName || '',
          authorizedPerson: data.authorizedPerson || '',
          taxNumber: data.taxNumber || '',
          taxOffice: data.taxOffice || '',
          address: data.address || '',
          city: data.city || '',
          district: data.district || '',
          phone: data.phone || '',
          email: data.email || '',
          debitBalance: data.debitBalance || 0,
          creditBalance: data.creditBalance || 0,
          balance: data.creditBalance || 0,
          calculatedBalance: (data.creditBalance || 0) - (data.debitBalance || 0)
        }));
      
      setAccounts(accountsList);
      setFilteredAccounts(accountsList);
      console.log(`✅ ${accountsList.length} adet cari hesap yüklendi (SQL)`);
      
    } catch (error) {
      console.error('❌ SQL cari hesap yükleme hatası:', error);
      setError(handleApiError(error));
      setAccounts([]);
    }
  }, []);
  
  // Para birimlerini yükleme (SQL döviz ayarları + Firebase yedek)
  const loadCurrencies = useCallback(async (compId: string): Promise<{code: string, active: boolean}[]> => {
    try {
      const currencies = await loadCompanyActiveCurrencies(compId);
      console.log('Aktif para birimleri yüklendi:', currencies);
      return currencies;
    } catch (error) {
      console.error('Para birimi yüklenirken hata:', error);
      return [{ code: 'TRY', active: true }];
    }
  }, []);
  
  // Fatura numarası oluşturma
  const generateInvoiceNumber = useCallback(async (compId: string): Promise<string> => {
    if (!compId) {
      console.warn('⚠️ [FATURA NO] companyId yok');
      return '';
    }

    let currentCompanyName = companyName;
    if (!currentCompanyName) {
      try {
        const companyInfo = await getCompanyInfo(compId);
        if (companyInfo?.companyName) {
          currentCompanyName = companyInfo.companyName;
        }
      } catch (err) {
        console.error('❌ [ŞİRKET] Şirket adı alınırken hata:', err);
      }
    }

    try {
      const { data: result } = await accountingApi.get(
        `${ACCOUNTING_API.INVOICE_PREVIEW_NUMBER}/${compId}/PURCHASE?companyName=${encodeURIComponent(currentCompanyName || '')}`
      );
      if (result.success && result.invoiceNumber) {
        console.log('📋 [PREVIEW] Sonraki fatura numarası:', result.invoiceNumber);
        return result.invoiceNumber;
      }
    } catch (err) {
      console.error('❌ [PREVIEW] Fatura numarası alınırken hata:', err);
    }
    return '';
  }, [companyName]);
  
  // Düzenleme için fatura yükleme
  const loadInvoiceForEditing = useCallback(async (
    invoiceId: string, 
    currentCompanyId: string,
    navigate: (path: string) => void
  ): Promise<{
    invoice: Invoice | null;
    account: Account | null;
    items: InvoiceItem[];
  }> => {
    try {
      console.log('📝 [DÜZENLEME] Fatura yükleniyor (SQL):', { invoiceId, companyId: currentCompanyId });
      
      const { data: result } = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_ID(invoiceId));
      if (!result.success || !result.data) {
        throw new Error('Fatura bulunamadı');
      }
      
      const sqlData = result.data;
      console.log('✅ [DÜZENLEME] Fatura yüklendi (SQL)');
      
      // E-fatura kontrolü
      if (sqlData.eInvoiceETTN || sqlData.EInvoiceETTN) {
        console.warn('⚠️ [DÜZENLEME] E-fatura ile gelen alış faturası düzenlenemez');
        setError(`❌ Bu fatura e-fatura sistemi üzerinden alınmıştır ve düzenlenemez.\n\nFatura No: ${sqlData.invoiceNumber}\nETTN: ${sqlData.eInvoiceETTN || sqlData.EInvoiceETTN}\n\n4 saniye sonra fatura listesine yönlendirileceksiniz...`);
        setTimeout(() => navigate('/fatura/alis'), 4000);
        return { invoice: null, account: null, items: [] };
      }
      
      // SQL formatını Invoice formatına dönüştür
      const data: Invoice = {
        id: sqlData.id,
        invoiceNumber: sqlData.invoiceNumber,
        projectId: sqlData.projectId ?? null,
        expenseCategoryId: sqlData.expenseCategoryId ?? null,
        expenseCategoryName: sqlData.expenseCategoryName ?? null,
        date: sqlData.invoiceDate ? new Date(sqlData.invoiceDate).toISOString().split('T')[0] : '',
        dueDate: sqlData.dueDate ? new Date(sqlData.dueDate).toISOString().split('T')[0] : '',
        accountId: sqlData.accountId,
        account: {
          companyName: sqlData.accountName || '',
          taxNumber: sqlData.accountTaxNumber,
          taxOffice: sqlData.accountTaxOffice,
          address: sqlData.accountAddress,
          phone: sqlData.accountPhone,
          email: sqlData.accountEmail,
        },
        items: (sqlData.items || []).map((item: any, index: number) => ({
          id: item.id?.toString() || `loaded_${Date.now()}_${index}`,
          itemId: item.itemId || `manual_${item.id}`,
          itemName: item.itemName,
          quantity: item.quantity || 0,
          displayQuantity: formatQuantityForDisplay(item.quantity || 0),
          unit: item.unit || 'Adet',
          enteredPrice: item.enteredPrice !== undefined && item.enteredPrice !== null ? item.enteredPrice : (item.unitPrice || 0),
          priceType: (item.priceType || 'VAT_EXCLUDED') as 'VAT_EXCLUDED' | 'VAT_INCLUDED',
          unitPrice: item.unitPrice || 0,
          unitPriceWithVat: item.unitPriceWithVat !== undefined && item.unitPriceWithVat !== null 
            ? item.unitPriceWithVat 
            : (item.unitPrice || 0) * (1 + (item.vatRate || 0) / 100),
          vatRate: item.vatRate || 0,
          sctRate: item.sctRate || 0,
          discountRate: item.discountRate || 0,
          discountAmount: item.discountAmount || 0,
          discountType: (sqlData.discountType === 'AMOUNT' ? 'AMOUNT' : 'PERCENT') as 'PERCENT' | 'AMOUNT',
          lineTotal: item.lineTotal || 0,
          netTotal: item.netTotal || 0,
          vatAmount: item.vatAmount || 0,
          sctAmount: item.sctAmount || 0,
          grossTotal: item.grossTotal || 0,
          warehouseId: item.warehouseId || undefined,
          ...readInvoiceLot(item),
        })),
        notes: sqlData.notes,
        currency: sqlData.currency || 'TRY',
        exchangeRate: sqlData.exchangeRate || 1,
        discountType: (sqlData.discountType === 'AMOUNT' ? 'AMOUNT' : 'PERCENT'),
        tevkifatRate: sqlData.tevkifatRate,
        tevkifatType: sqlData.tevkifatType,
        tevkifatAmount: sqlData.tevkifatAmount,
        stopajRate: sqlData.stopajRate,
        stopajAmount: sqlData.stopajAmount,
        subtotal: sqlData.subtotal,
        vatTotal: sqlData.vatTotal,
        sctTotal: sqlData.sctTotal,
        discountTotal: sqlData.discountTotal,
        grandTotal: sqlData.grandTotal,
        originalInvoiceTotal: sqlData.originalInvoiceTotal,
        createdAt: sqlData.createdAt || new Date().toISOString(),
        branchId: sqlData.branchId || null,
        status: sqlData.status || 'completed',
      };
      
      // Account bilgilerini yükle
      let account: Account | null = null;
      if (data.accountId) {
        try {
          const { data: accountsResult } = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
          const accountsList = Array.isArray(accountsResult) ? accountsResult : (accountsResult?.data ?? []);
            
          const matchedAccount = accountsList.find((acc: any) => 
            acc.id?.toString() === data.accountId || 
            acc.firebaseId === data.accountId
          );
            
          if (matchedAccount) {
            account = {
              id: matchedAccount.firebaseId || matchedAccount.id?.toString(),
              companyName: matchedAccount.companyName || data.account.companyName,
              authorizedPerson: matchedAccount.authorizedPerson || '',
              taxNumber: matchedAccount.taxNumber || data.account.taxNumber,
              taxOffice: matchedAccount.taxOffice || data.account.taxOffice,
              address: matchedAccount.address || data.account.address,
              city: matchedAccount.city || '',
              district: matchedAccount.district || '',
              phone: matchedAccount.phone || data.account.phone || '',
              email: matchedAccount.email || data.account.email || '',
              debitBalance: matchedAccount.debitBalance || 0,
              creditBalance: matchedAccount.creditBalance || 0,
              balance: matchedAccount.balance || 0,
              calculatedBalance: matchedAccount.calculatedBalance || 0,
            };
          }
        } catch (err) {
          console.error('❌ [DÜZENLEME] Account bilgileri yüklenemedi:', err);
        }
        
        // Fallback
        if (!account) {
          account = {
            id: data.accountId,
            companyName: data.account.companyName,
            authorizedPerson: '',
            taxNumber: data.account.taxNumber,
            taxOffice: data.account.taxOffice,
            address: data.account.address,
            city: '',
            district: '',
            phone: data.account.phone || '',
            email: data.account.email || '',
            debitBalance: 0,
            creditBalance: 0,
            balance: 0,
            calculatedBalance: 0,
          };
        }
      }
      
      return { invoice: data, account, items: data.items };
      
    } catch (err) {
      console.error("❌ [DÜZENLEME] Fatura yükleme hatası:", err);
      setError('Fatura bilgileri yüklenirken bir hata oluştu.');
      navigate('/fatura/alis');
      return { invoice: null, account: null, items: [] };
    }
  }, []);
  
  // Teklif verilerini yükleme
  const loadOfferData = useCallback(async (): Promise<{
    account: Account | null;
    items: InvoiceItem[];
    offerData: any;
  }> => {
    try {
      const offerDataStr = localStorage.getItem('offerToInvoice');
      if (!offerDataStr) {
        return { account: null, items: [], offerData: null };
      }
      
      const offerData = JSON.parse(offerDataStr);
      localStorage.removeItem('offerToInvoice');
      
      console.log('Teklif verisi yüklendi:', offerData);
      
      let account: Account | null = null;
      
      const supplierNumericId = parseInt(String(offerData.supplierId ?? ''), 10);
      const supplierIsNumeric = !isNaN(supplierNumericId) && supplierNumericId.toString() === String(offerData.supplierId);

      if (offerData.supplierId && companyId && supplierIsNumeric) {
        const detailResponse = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_ID(supplierNumericId));
        const accountData = detailResponse.data?.data || detailResponse.data;

        if (accountData) {
          account = {
            id: offerData.supplierId,
            companyName: accountData.companyName || offerData.supplier?.companyName || '',
            authorizedPerson: accountData.authorizedPerson || '',
            taxNumber: accountData.taxNumber || offerData.supplier?.taxNumber || '',
            taxOffice: accountData.taxOffice || offerData.supplier?.taxOffice || '',
            address: accountData.address || offerData.supplier?.address || '',
            city: accountData.city || '',
            district: accountData.district || '',
            phone: accountData.phone || offerData.supplier?.phone || '',
            email: accountData.email || offerData.supplier?.email || '',
            debitBalance: accountData.debitBalance,
            creditBalance: accountData.creditBalance,
            balance: accountData.balance,
            calculatedBalance: accountData.calculatedBalance
          };
        }
      }
      
      const defaultWarehouse = warehouses.find(w => w.isDefault);
      
      let items: InvoiceItem[] = [];
      if (offerData.items && offerData.items.length > 0) {
        items = offerData.items.map((item: any) => {
          const quantity = item.quantity || 0;
          const unitPrice = item.unitPrice || 0;
          const vatRate = item.vatRate || 0;
          const sctRate = item.sctRate || 0;
          const discountRate = item.discountRate || 0;
          
          const lineTotal = quantity * unitPrice;
          const discountAmount = lineTotal * (discountRate / 100);
          const netTotal = lineTotal - discountAmount;
          const vatAmount = netTotal * (vatRate / 100);
          const sctAmount = netTotal * (sctRate / 100);
          const grossTotal = netTotal + vatAmount + sctAmount;
          
          return {
            id: Date.now() + '_' + Math.random().toString(36).substr(2, 9),
            itemId: item.itemId || 'manual_' + Date.now(),
            itemName: item.itemName || item.name || '',
            quantity,
            unit: item.unit || 'adet',
            enteredPrice: unitPrice,
            priceType: 'VAT_EXCLUDED' as const,
            unitPrice,
            unitPriceWithVat: unitPrice * (1 + vatRate / 100),
            vatRate,
            sctRate,
            discountRate,
            discountAmount,
            lineTotal,
            netTotal,
            vatAmount,
            sctAmount,
            grossTotal,
            warehouseId: defaultWarehouse?.id || '',
            displayQuantity: formatQuantityForDisplay(quantity)
          };
        });
      }
      
      return { account, items, offerData };
      
    } catch (error) {
      console.error('Teklif verisi yüklenirken hata:', error);
      return { account: null, items: [], offerData: null };
    }
  }, [companyId, warehouses]);
  
  // Cari arama handlers
  const handleAccountSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value.toLowerCase();
    setAccountSearchTerm(searchTerm);
    
    if (searchTerm.trim() === '') {
      setFilteredAccounts(accounts);
    } else {
      const filtered = accounts.filter(account => 
        account.companyName.toLowerCase().includes(searchTerm) ||
        account.authorizedPerson.toLowerCase().includes(searchTerm) ||
        account.taxNumber.toLowerCase().includes(searchTerm) ||
        (account.phone && account.phone.toLowerCase().includes(searchTerm))
      );
      setFilteredAccounts(filtered);
    }
    setAccountsPage(0);
  }, [accounts]);
  
  const handleAccountsPageChange = useCallback((
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setAccountsPage(newPage);
  }, []);
  
  const handleAccountsRowsPerPageChange = useCallback((
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setAccountsRowsPerPage(parseInt(event.target.value, 10));
    setAccountsPage(0);
  }, []);
  
  // Ürün sayfalama handlers
  const handleItemSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setItemSearchTerm(event.target.value);
  }, []);
  
  const handleItemsPageChange = useCallback((
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setItemsPage(newPage);
  }, []);
  
  const handleItemsRowsPerPageChange = useCallback((
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setItemsRowsPerPage(parseInt(event.target.value, 10));
    setItemsPage(0);
  }, []);
  
  // Hesaplar yüklendiğinde filtrelenmiş hesapları da güncelle
  useEffect(() => {
    setFilteredAccounts(accounts);
  }, [accounts]);
  
  // İlk yükleme - CompanyId al
  useEffect(() => {
    const initCompanyId = async () => {
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setError('Kullanıcı oturumu bulunamadı.');
        setLoading(false);
        return;
      }
      
      const userData = await firebaseDataService.getUserData(uid);
      
      if (userData && userData.companyId) {
        setCompanyId(userData.companyId);
        setLoading(false);
      } else {
        setError('Kullanıcı bilgileri bulunamadı.');
        setLoading(false);
      }
    };
    
    initCompanyId();
  }, []);
  
  return {
    // State
    companyId,
    loading,
    setLoading,
    error,
    setError,
    accounts,
    filteredAccounts,
    setFilteredAccounts,
    
    // Cari arama
    accountSearchTerm,
    setAccountSearchTerm,
    accountsPage,
    accountsRowsPerPage,
    handleAccountSearch,
    handleAccountsPageChange,
    handleAccountsRowsPerPageChange,
    
    // Ürün sayfalama
    itemsPage,
    itemsRowsPerPage,
    itemSearchTerm,
    setItemSearchTerm,
    handleItemSearch,
    handleItemsPageChange,
    handleItemsRowsPerPageChange,
    
    // Modal state'ler
    isSuccessModalOpen,
    setIsSuccessModalOpen,
    isDraftModalOpen,
    setIsDraftModalOpen,
    draftData,
    setDraftData,
    showDraftSavedAlert,
    setShowDraftSavedAlert,
    isPdfModalOpen,
    setIsPdfModalOpen,
    isEInvoiceModalOpen,
    setIsEInvoiceModalOpen,
    isAccountMatchModalOpen,
    setIsAccountMatchModalOpen,
    
    // E-fatura
    eInvoiceUsername,
    setEInvoiceUsername,
    eInvoicePassword,
    setEInvoicePassword,
    eInvoiceSupplierInfo,
    setEInvoiceSupplierInfo,
    
    // Stok işleme modal
    isStockProcessModalOpen,
    setIsStockProcessModalOpen,
    stockProcessInvoiceId,
    setStockProcessInvoiceId,
    stockProcessCurrentItem,
    setStockProcessCurrentItem,
    stockProcessTotalItems,
    setStockProcessTotalItems,
    stockProcessSuccess,
    setStockProcessSuccess,
    
    // Fonksiyonlar
    loadAccounts,
    loadCurrencies,
    generateInvoiceNumber,
    loadInvoiceForEditing,
    loadOfferData,
  };
};
